import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "api/axios";
// AWS SDK
import AWS from "aws-sdk";
// Chakra imports
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Switch,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
// Assets
import CoverImage from "assets/img/da-dashboard-bg.jpg";
import AuthCover from "layouts/AuthCover";

// Auth
import useAuth from "hooks/useAuth";

const LOGIN_URL = "/auth/SignIn";

function SignInCover() {
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  const navigate = useNavigate();
  const lastLocation = JSON.parse(localStorage.getItem("lastLocation"));

  // Chakra color mode
  const textColor = useColorModeValue("black", "white");
  const bgForm = useColorModeValue("white", "navy.800");
  const inputColor = useColorModeValue("white", "#343A40");
  const buttonColor = useColorModeValue("blue.500", "#343A40");

  // Auth
  const [isLoading, setIsLoading] = useState(true);
  const { auth, setAuth, persist, setPersist, isAdminOrSuperAdmin } = useAuth();
  const userRef = useRef();
  const pwRef = useRef();
  const errRef = useRef();
  const [user, setUser] = useState("");
  const [pwd, setPwd] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const toast = useToast();

  useEffect(() => {
    const loggedInUser = localStorage.getItem("user");
    if (loggedInUser) {
      setUser(loggedInUser);
      pwRef.current.focus();
    } else {
      userRef.current.focus();
    }
  }, []);

  useEffect(() => {
    setErrMsg("");
  }, [user, pwd]);

  useEffect(() => {
    localStorage.setItem("persist", persist);
  }, [persist]);

  const handleSignIn = async () => {
    try {
      const response = await axios.post(
        LOGIN_URL,
        JSON.stringify({ user, pwd }),
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      const accessToken = response?.data?.accessToken;
      const roles = response?.data?.roles;
      localStorage.setItem("user", user);
      await setAuth({ user, roles, accessToken });
      setUser("");
      setPwd("");
      toast({
        title: "Logging In...",
        description: "Please wait...",
        status: "loading",
        duration: 2000,
      });
      setTimeout(() => {
        toast({
          title: "Login Successful",
          description: "Loading Dashboard...",
          status: "success",
          duration: 2000,
        });
        navigate("/admin/dashboard", { replace: true });
      }, 2000);
    } catch (err) {
      if (!err?.response) {
        toast({
          title: "Login Rejected",
          description: "No server response. :(",
          status: "error",
          duration: 3000,
        });
        console.log(err);
      } else if (err.response?.status === 400) {
        toast({
          title: "Login Rejected",
          description: "Missing Username or Password.",
          status: "error",
          duration: 3000,
        });
      } else if (err.response?.status === 401) {
        toast({
          title: "Login Rejected",
          description: err.response?.data,
          status: "error",
          duration: 3000,
        });
      } else {
        toast({
          title: "Login Rejected",
          description: "Login failed. Please contact Lex. :(",
          status: "error",
          duration: 3000,
        });
      }
    }
  };

  const togglePersist = () => {
    setPersist((prev) => !prev);
  };

  const handleSwitch = (roles) => {
    if (!roles.User) {
      console.log("Ah ah ah, you didn't say the magic word.");
    } else {
      !lastLocation
        ? navigate("/admin/dashboard", { replace: true })
        : navigate(lastLocation.pathname, { replace: true });
    }
  };

  return (
    <AuthCover image={CoverImage} position="relative">
      <Flex
        w="100%"
        h="100vh" // Ensure the Flex container covers the full viewport height
        alignItems="center"
        justifyContent="center"
      >
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "90%", md: "50%" }} // Adjust the width based on screen size
          background="transparent"
          borderRadius="4px"
          p="40px"
          bg={useColorModeValue("white", "#495057")}
          boxShadow={useColorModeValue(
            "0px 5px 14px rgba(0, 0, 0, 0.05)",
            "unset"
          )}
        >
          <Text
            fontSize="xl"
            color={textColor}
            fontWeight="bold"
            textAlign="center"
            mb="22px"
          >
            Sign In
          </Text>
          <InputGroup size="lg">
            <Input
              variant="auth"
              ref={userRef}
              onChange={(e) => setUser(e.target.value)}
              value={user}
              color={textColor}
              bg={inputColor}
              fontSize="sm"
              type="text"
              placeholder="username"
              mb="24px"
            />
          </InputGroup>
          <InputGroup size="lg">
            <Input
              variant="auth"
              ref={pwRef}
              fontSize="sm"
              pr="4.5rem"
              color={textColor}
              bg={inputColor}
              onChange={(e) => setPwd(e.target.value)}
              value={pwd}
              type={show ? "text" : "password"}
              placeholder="Enter password"
            />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={handleClick}>
                {show ? "Hide" : "Show"}
              </Button>
            </InputRightElement>
          </InputGroup>
          <Box display="flex" alignItems="center" my="24px">
            <Switch isChecked={persist} onChange={togglePersist} />
            <Text htmlFor="remember-login" mb="0" fontWeight="normal">
              Remember me
            </Text>
          </Box>
          <Button
            fontSize="12px"
            color={useColorModeValue("white", "white")}
            bg={buttonColor}
            fontWeight="bold"
            w="100%"
            h="45"
            onClick={() => handleSignIn()}
          >
            SIGN IN
          </Button>
        </Flex>
      </Flex>
    </AuthCover>
  );
}

export default SignInCover;
